import { useEffect, type ReactNode } from 'react';
import { navigate } from '../router';
import { Ic } from '../st8n-tickets/kit.jsx';

/** Name the browser tab for a screen ("Settings · Deleg8", what a screen reader announces on arrival); the app's own name
 *  (Deleg8, or a portal's company) comes back when the screen goes. A screen without <Page> (a board) can call it itself. */
export function useTitle(title: string | null | undefined) {
  useEffect(() => {
    if (!title) return;
    const base = document.title;
    document.title = `${title} · ${base}`;
    return () => { document.title = base; };
  }, [title]);
}

/** The way back, above a screen's title ("< Settings"): a real link (open in a new tab works), 44px tall, one look everywhere. */
export function BackLink({ to, label, testId }: { to: string; label: ReactNode; testId?: string }) {
  return (
    <a href={to} onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return; e.preventDefault(); navigate(to); }} data-testid={testId}
      className="-ml-2 mb-2 inline-flex min-h-11 items-center gap-1 rounded-xl px-2 text-[15px] font-medium text-accent-text"><Ic.Back width={18} height={18} /> {label}</a>
  );
}

/** A plain screen: the way back (`back`), a title, an optional line under it, and a scrolling body sized for a phone and a desk. */
export function Page({ title, sub, right, back, children, testId }: { title: string; sub?: ReactNode; right?: ReactNode; back?: { to: string; label: ReactNode }; children: ReactNode; testId?: string }) {
  useTitle(title);
  return (
    <div className="scroll-thin h-full overflow-y-auto pb-24 lg:pb-10" data-testid={testId}>
      <div className="mx-auto max-w-[920px] px-4 pt-6 lg:px-8 lg:pt-9">
        {back ? <BackLink to={back.to} label={back.label} /> : null}
        {/* The button sits beside the title; the line under it runs the full width (beside the button it wrapped to a narrow column on a phone). */}
        <header className="mb-6">
          <div className="flex items-center gap-3">
            <h1 className="min-w-0 flex-1 text-[26px] font-semibold tracking-tight lg:text-[30px]">{title}</h1>
            {right}
          </div>
          {sub ? <p className="mt-1 text-[14px] text-dim">{sub}</p> : null}
        </header>
        {children}
      </div>
    </div>
  );
}

export function Section({ title, aside, children, testId }: { title: string; aside?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <section className="mb-8" data-testid={testId}>
      <div className="mb-2.5 flex items-center gap-2"><h2 className="section-title">{title}</h2><span className="ml-auto">{aside}</span></div>
      {children}
    </section>
  );
}

/** A screen that could not load: the reason in words and a way to try again (never an empty screen). */
export function LoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const text = error instanceof Error && error.message ? error.message : 'Something went wrong.';
  return (
    <div role="alert" data-testid="load-error" className="m-4 flex flex-wrap items-center gap-3 rounded-2xl bg-bad-soft p-4 text-bad-text lg:m-6">
      <span className="min-w-0 flex-1 text-[14.5px]">{text}</span>
      <button type="button" className="btn-line btn-sm" onClick={onRetry} data-testid="retry">Retry</button>
    </div>
  );
}

/** Nothing here yet: one look for every empty list (a dashed box, a quiet icon, the words, and what to do about it). */
export function Empty({ icon, text, action, testId }: { icon?: ReactNode; text: ReactNode; action?: ReactNode; testId?: string }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-dashed border-line-2 p-4 text-[13.5px] text-dim" data-testid={testId}>
      {icon ? <span className="text-faint" aria-hidden="true">{icon}</span> : null}
      <span className="min-w-0 flex-1">{text}</span>
      {action}
    </div>
  );
}

/** Still loading: grey rows the size of what is coming (never a blank screen, never a spinner inside a page); `title` puts a
 *  heading-sized bar first (a request or a record about to open). */
export function Skeleton({ rows = 2, height = 64, title, testId }: { rows?: number; height?: number; title?: boolean; testId?: string }) {
  return (
    <div className="grid gap-2" aria-busy="true" aria-label="Loading" role="status" data-testid={testId}>
      {title ? <div className="skeleton mb-1 h-8 w-3/4" /> : null}
      {Array.from({ length: rows }, (_, i) => <div key={i} className="skeleton rounded-2xl" style={{ height }} />)}
    </div>
  );
}
